
// Libraries
import { defineComponent } from 'vue';

// Components
import LoginActionButton from 'client/components/LoginActionButton.vue';
import LoginTextField from 'client/components/LoginTextField.vue';

// Constants and Types
import { useLoginLabels } from 'client/util/Constants/LoginLabels';

// Utils
import routerService from 'client/services/routerService';

// Store
import { mapStores } from 'pinia';
import IconButton from 'client/components/IconButton.vue';
import UserLoginStore from 'client/stores/UserLoginStore';

export default defineComponent({
  name: 'PasswordEntry',

  components: {
    LoginActionButton,
    LoginTextField,
    IconButton,
  },

  props: {
    context: {
      type: String,
      default: '',
    },
    controllingQueryParams: {
      type: Object,
      default: () => ({}),
    },
    onCredentialsSubmit: {
      type: Function,
      default: () => {},
    },
  },

  setup() {
    const uiLabel = useLoginLabels();
    return { uiLabel };
  },

  beforeDestroy() {
    this.userLoginStore.password = '';
  },

  data() {
    return {
      isLoading: false,
      isPasswordVisible: false,
      passwordTimer: undefined as number | undefined,
      helpdeskMessagePrefix: 'If you face login issue, please contact your respective ',
      helpdeskLinkText: 'helpdesk here',
      forgotPasswordText: 'Forgot my password',
    };
  },

  computed: {
    ...mapStores(UserLoginStore),

    disableButton(): boolean {
      return !this.userLoginStore.password.length;
    },

    isContextSso(): boolean {
      return this.context === 'sso';
    },

    inputType(): string {
      return this.isPasswordVisible ? 'text' : 'password';
    },

    icon(): string {
      return this.isPasswordVisible ? 'viewableOff' : 'viewable';
    },

    emailSubmitResult(): any {
      return this.userLoginStore.emailSubmitResult;
    },

    logoUrl(): string {
      const emailResult = this.emailSubmitResult;
      const helpdeskContactInfo = emailResult?.helpdeskContactInfo;

      if (helpdeskContactInfo?.include_logo && helpdeskContactInfo?.logo_url) {
        return helpdeskContactInfo.logo_url;
      }
      return '';
    },

    hasHelpdeskInfo(): boolean {
      const emailResult = this.emailSubmitResult;
      const helpdeskContactInfo = emailResult?.helpdeskContactInfo;
      return !!(helpdeskContactInfo?.config && helpdeskContactInfo.config.length > 0);
    },
  },

  methods: {
    handleError(error: any) {
      this.$emit('error', error);
    },

    handleSuccess() {
      this.$emit('success');
    },

    resetErrorMessage() {
      this.$emit('resetErrorMessage');
    },

    resetPasswordRequest() {
      routerService.goToUserPasswordResetRequest({ query: { ...this.controllingQueryParams } });
    },

    goToHelpdesk() {
      routerService.goToUserHelpdeskInfo({ query: { ...this.controllingQueryParams } });
    },

    async submit() {
      this.resetErrorMessage();
      this.isLoading = true;

      try {
        await this.onCredentialsSubmit(this.userLoginStore.$state);
        this.handleSuccess();
      } catch (error) {
        this.handleError(error);
      } finally {
        this.isLoading = false;
      }
    },

    togglePasswordVisibility() {
      this.isPasswordVisible = !this.isPasswordVisible;
      clearTimeout(this.passwordTimer);
      this.passwordTimer = setTimeout(() => {
        this.isPasswordVisible = false;
      }, 4000);
    },
  },
});
